import React, { memo, useRef } from 'react';
import { Logger } from '@tcp-base/toolkit';

/**
 * shallowEqual is a function that checks if two objects are shallowly equal.
 * @param first - The first object to compare.
 * @param second - The second object to compare.
 * @returns True if the objects are shallowly equal, false otherwise.
 */
export function shallowEqual<T extends object>(first: T, second: T): boolean {
  if (first === second) return true;
  const firstKeys = Object.keys(first) as (keyof T)[];
  const secondKeys = Object.keys(second) as (keyof T)[];
  if (firstKeys.length !== secondKeys.length) return false;
  for (const k of firstKeys) {
    if (first[k] !== second[k]) return false;
  }
  return true;
}

/**
 * useShallowStable is a hook that returns a stable value.
 * @param value - The value to return.
 * @returns The stable value.
 */
export function useShallowStable<T extends object>(value: T): T {
  const ref = useRef(value);
  if (!shallowEqual(ref.current, value)) {
    ref.current = value;
  }
  return ref.current;
}

/**
 * Type guard to check if a value is a plain object
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date)
  );
}

/**
 * Identifies the type and formats a value for logging
 * @param value - The value to format
 * @returns A formatted representation of the value
 */
function formatValue(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'function') return '[Function]';
  if (typeof value === 'string') return `"${value}"`;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return `Array(${value.length})`;
  if (isPlainObject(value)) return `Object {${Object.keys(value).length} keys}`;
  if (value instanceof Date) return `Date(${value.toISOString()})`;
  return String(value);
}

/**
 * Compares two models and returns only the changed properties with formatted output
 * @param previous - The previous model
 * @param current - The current model
 * @returns An object containing the changes with before/after values
 */
export function getModelChanges<T extends object>(
  previous: T | null,
  current: T,
): Record<string, { previous: unknown; current: unknown; formatted: string }> {
  const changes: Record<string, { previous: unknown; current: unknown; formatted: string }> = {};

  // If no previous model, all properties are new
  if (!previous) {
    const currentKeys = Object.keys(current) as (keyof T)[];
    currentKeys.forEach((key) => {
      const value = current[key];
      changes[String(key)] = {
        previous: undefined,
        current: value,
        formatted: `[NEW] ${formatValue(value)}`,
      };
    });
    return changes;
  }

  // Get all unique keys from both objects
  const allKeys = new Set([
    ...(Object.keys(previous) as (keyof T)[]),
    ...(Object.keys(current) as (keyof T)[]),
  ]);

  // Compare each property
  allKeys.forEach((key) => {
    const prevValue = previous[key];
    const currValue = current[key];

    // Check if values are different
    if (prevValue !== currValue) {
      changes[String(key)] = {
        previous: prevValue,
        current: currValue,
        formatted: `${formatValue(prevValue)} → ${formatValue(currValue)}`,
      };
    }
  });

  return changes;
}

/**
 * Logs model changes in a readable format using appropriate Logger methods
 * @param componentName - The name of the component for logging context
 * @param previous - The previous model
 * @param current - The current model
 */
export function logModelChanges<T extends object>(
  componentName: string,
  previous: T | null,
  current: T,
): void {
  const changes = getModelChanges(previous, current);
  const changeCount = Object.keys(changes).length;

  if (changeCount === 0) {
    Logger.log(`[${componentName}] No model changes detected`);
    return;
  }

  Logger.log(`[${componentName}] Model changes detected (${changeCount} properties):`);

  // Log each change with appropriate formatting
  Object.entries(changes).forEach(([key, change]) => {
    // Use different log methods based on value types
    const { current: curr, formatted } = change;

    // For complex types (objects/arrays), use group logging
    if (isPlainObject(curr) || Array.isArray(curr)) {
      Logger.log(`  • ${key}: ${formatted}`);
      if (isPlainObject(curr) && Object.keys(curr).length < 10) {
        Logger.log(`    Details:`, curr);
      } else if (Array.isArray(curr) && curr.length < 10) {
        Logger.log(`    Details:`, curr);
      }
    } else {
      // For primitives, log inline
      Logger.log(`  • ${key}: ${formatted}`);
    }
  });
}

/**
 * MVCConfig is a type that defines the configuration for the withMVC function.
 * @param Props - The props for the MVC component.
 * @param Model - The model for the MVC component.
 */
interface MVCConfig<Props, Model extends object> {
  readonly Viewer: React.FC<Model>;
  readonly useController: (props: Props) => Model | null;
  readonly options?: {
    readonly logModelChanges?: boolean;
    /** Disable returning a cached render tree; required when the Viewer uses web components. */
    readonly disableRenderCache?: boolean;
  };
}
/**
 * withMVC: ties together
 * - a Viewer (render-only component that takes Model as props)
 * - a Controller hook (maps Props -> Model)
 * - returns a memoized component
 */
export function withMVC<Props, Model extends object>({
  Viewer,
  useController,
  options = {
    logModelChanges: false,
  },
}: MVCConfig<Props, Model>): React.MemoExoticComponent<React.FC<Props>> {
  const { logModelChanges: shouldLogModelChanges = false, disableRenderCache = false } = options;

  const MVCWrapper = (props: Props): React.ReactElement | null => {
    const model = useController(props);
    const stableModel = useShallowStable(model || ({} as Model));
    const previousModelRef = useRef<Model | null>(null);
    const previousRenderRef = useRef<React.ReactElement | null>(null);

    if (!model) return null;

    // If previous model is same as current model, don't render again.
    // Disabled for viewers that host web components, where a cached element tree
    // can desync React reconciliation from the live DOM (insertBefore errors).
    if (
      !disableRenderCache &&
      previousModelRef.current === stableModel &&
      previousRenderRef.current
    ) {
      Logger.log(`MVCWrapper: ${Viewer.name} : Skipping render - model unchanged`);
      return previousRenderRef.current;
    }

    if (shouldLogModelChanges) {
      logModelChanges(Viewer.name || 'Anonymous', previousModelRef.current, stableModel);
    }
    const rendered = <Viewer {...stableModel} />;

    // Store current model and render result for next comparison
    previousModelRef.current = stableModel;
    previousRenderRef.current = rendered;

    return rendered;
  };

  MVCWrapper.displayName = `withMVC(${Viewer.name || 'Anonymous'})`;

  const MemoizedComponent = memo(MVCWrapper);
  MemoizedComponent.displayName = `withMVC(${Viewer.name || 'Anonymous'})`;

  return MemoizedComponent;
}

export default withMVC;
